export const TARGET_SIZE_MIN_BYTES = 10 * 1024
export const TARGET_SIZE_MAX_BYTES = 50 * 1024 * 1024
export const TARGET_SIZE_MIN_QUALITY = 30
export const TARGET_SIZE_MAX_QUALITY = 95
export const TARGET_SIZE_MIN_LONG_SIDE = 320
export const TARGET_SIZE_MAX_ATTEMPTS = 14
export const TARGET_SIZE_COOPERATIVE_BUDGET_MS = 8_000

export type TargetSizeStopReason =
  | 'minimum_quality_and_dimensions'
  | 'attempt_limit'
  | 'time_limit'

export interface TargetSizeCandidate {
  blob: Blob
  quality: number
  width: number
  height: number
}

export interface TargetSizeSearchResult extends TargetSizeCandidate {
  reachedTarget: boolean
  attempts: number
  stopReason?: TargetSizeStopReason
}

interface EncodeTargetCandidate {
  quality: number
  width: number
  height: number
  signal: AbortSignal
}

interface TargetSizeSearchOptions {
  sourceWidth: number
  sourceHeight: number
  targetBytes: number
  signal: AbortSignal
  encode: (candidate: EncodeTargetCandidate) => Promise<Blob>
  maxAttempts?: number
  cooperativeBudgetMs?: number
  now?: () => number
  yieldControl?: () => Promise<void>
}

function abortError(): Error {
  const error = new Error('Target-size compression aborted')
  error.name = 'AbortError'
  return error
}

function assertActive(signal: AbortSignal): void {
  if (signal.aborted) {
    throw abortError()
  }
}

function defaultYieldControl(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0)
  })
}

function dimensionsForScale(
  sourceWidth: number,
  sourceHeight: number,
  scale: number,
): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  }
}

function sameDimensions(
  left: { width: number; height: number },
  right: { width: number; height: number },
): boolean {
  return left.width === right.width && left.height === right.height
}

function betterMinimum(
  current: TargetSizeCandidate | null,
  candidate: TargetSizeCandidate,
): TargetSizeCandidate {
  return !current || candidate.blob.size < current.blob.size ? candidate : current
}

function betterReached(
  current: TargetSizeCandidate | null,
  candidate: TargetSizeCandidate,
): TargetSizeCandidate {
  if (!current) return candidate
  const currentPixels = current.width * current.height
  const candidatePixels = candidate.width * candidate.height
  if (candidatePixels !== currentPixels) {
    return candidatePixels > currentPixels ? candidate : current
  }
  if (candidate.quality !== current.quality) {
    return candidate.quality > current.quality ? candidate : current
  }
  return candidate.blob.size > current.blob.size ? candidate : current
}

export function parseTargetBytes(value: string, unit: 'KB' | 'MB'): number | null {
  const numeric = Number(value.replace(',', '.'))
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null
  }
  const bytes = Math.round(numeric * (unit === 'MB' ? 1024 * 1024 : 1024))
  if (bytes < TARGET_SIZE_MIN_BYTES || bytes > TARGET_SIZE_MAX_BYTES) {
    return null
  }
  return bytes
}

export async function searchTargetSize(
  options: TargetSizeSearchOptions,
): Promise<TargetSizeSearchResult> {
  const {
    sourceWidth,
    sourceHeight,
    targetBytes,
    signal,
    encode,
    maxAttempts: requestedMaxAttempts = TARGET_SIZE_MAX_ATTEMPTS,
    cooperativeBudgetMs: requestedBudgetMs = TARGET_SIZE_COOPERATIVE_BUDGET_MS,
    now = () => performance.now(),
    yieldControl = defaultYieldControl,
  } = options
  const maxAttempts = Math.min(requestedMaxAttempts, TARGET_SIZE_MAX_ATTEMPTS)
  const cooperativeBudgetMs = Math.min(
    requestedBudgetMs,
    TARGET_SIZE_COOPERATIVE_BUDGET_MS,
  )

  if (
    sourceWidth < 1 ||
    sourceHeight < 1 ||
    targetBytes < TARGET_SIZE_MIN_BYTES ||
    targetBytes > TARGET_SIZE_MAX_BYTES ||
    !Number.isFinite(maxAttempts) ||
    !Number.isFinite(cooperativeBudgetMs) ||
    maxAttempts < 1 ||
    cooperativeBudgetMs < 1
  ) {
    throw new Error('Invalid target-size search options')
  }

  const startedAt = now()
  let attempts = 0
  let minimum: TargetSizeCandidate | null = null
  let reached: TargetSizeCandidate | null = null

  const stopReason = (): TargetSizeStopReason | null => {
    if (attempts >= maxAttempts) return 'attempt_limit'
    if (now() - startedAt >= cooperativeBudgetMs) return 'time_limit'
    return null
  }

  const runEncode = async (
    quality: number,
    dimensions: { width: number; height: number },
  ): Promise<TargetSizeCandidate | null> => {
    assertActive(signal)
    if (stopReason()) return null
    await yieldControl()
    assertActive(signal)
    if (stopReason()) return null
    attempts += 1
    const blob = await encode({
      quality,
      width: dimensions.width,
      height: dimensions.height,
      signal,
    })
    assertActive(signal)
    const candidate = { blob, quality, ...dimensions }
    minimum = betterMinimum(minimum, candidate)
    if (blob.size <= targetBytes) {
      reached = betterReached(reached, candidate)
    }
    return candidate
  }

  const finishBounded = (reason: TargetSizeStopReason): TargetSizeSearchResult => {
    if (reached) {
      return { ...reached, reachedTarget: true, attempts }
    }
    if (!minimum) {
      throw new Error('Target-size search stopped before the first encode')
    }
    return {
      ...minimum,
      reachedTarget: false,
      attempts,
      stopReason: reason,
    }
  }

  const searchQuality = async (
    dimensions: { width: number; height: number },
    lowCandidate: TargetSizeCandidate,
    highQuality = TARGET_SIZE_MAX_QUALITY,
  ): Promise<TargetSizeSearchResult> => {
    let low = lowCandidate.quality
    let high = highQuality
    while (high - low > 1) {
      const reason = stopReason()
      if (reason) return finishBounded(reason)
      const quality = Math.ceil((low + high) / 2)
      const candidate = await runEncode(quality, dimensions)
      if (!candidate) return finishBounded(stopReason() ?? 'attempt_limit')
      if (candidate.blob.size <= targetBytes) {
        low = quality
      } else {
        high = quality
      }
    }
    return {
      ...(reached ?? lowCandidate),
      reachedTarget: true,
      attempts,
    }
  }

  const sourceDimensions = { width: sourceWidth, height: sourceHeight }
  const maximum = await runEncode(TARGET_SIZE_MAX_QUALITY, sourceDimensions)
  if (!maximum) return finishBounded(stopReason() ?? 'attempt_limit')
  if (maximum.blob.size <= targetBytes) {
    return { ...maximum, reachedTarget: true, attempts }
  }

  const sourceMinimum = await runEncode(TARGET_SIZE_MIN_QUALITY, sourceDimensions)
  if (!sourceMinimum) return finishBounded(stopReason() ?? 'attempt_limit')
  if (sourceMinimum.blob.size <= targetBytes) {
    return searchQuality(sourceDimensions, sourceMinimum)
  }

  const minScale = Math.min(
    1,
    TARGET_SIZE_MIN_LONG_SIDE / Math.max(sourceWidth, sourceHeight),
  )
  const minimumDimensions = dimensionsForScale(sourceWidth, sourceHeight, minScale)
  let upperScale = 1
  let lowerScale = minScale
  let lowerCandidate: TargetSizeCandidate | null = null
  let minimumGuardrailEvaluated = sameDimensions(sourceDimensions, minimumDimensions)
  const triedDimensions = new Set<string>()

  const estimatedScale = Math.max(
    minScale,
    Math.min(0.9, Math.sqrt(targetBytes / sourceMinimum.blob.size) * 0.96),
  )
  for (let index = 0; index < 4; index += 1) {
    const reason = stopReason()
    if (reason) return finishBounded(reason)

    const proposedScale =
      index === 0 ? estimatedScale : (lowerScale + upperScale) / 2
    const scale = Math.max(lowerScale, Math.min(upperScale, proposedScale))
    const dimensions = dimensionsForScale(sourceWidth, sourceHeight, scale)
    const dimensionsKey = `${dimensions.width}x${dimensions.height}`
    if (sameDimensions(dimensions, sourceDimensions) || triedDimensions.has(dimensionsKey)) {
      continue
    }
    triedDimensions.add(dimensionsKey)
    const candidate = await runEncode(TARGET_SIZE_MIN_QUALITY, dimensions)
    if (!candidate) return finishBounded(stopReason() ?? 'attempt_limit')
    if (sameDimensions(candidate, minimumDimensions)) {
      minimumGuardrailEvaluated = true
    }
    const actualScale = dimensions.width / sourceWidth
    if (candidate.blob.size <= targetBytes) {
      lowerScale = actualScale
      lowerCandidate = candidate
    } else {
      upperScale = actualScale
    }
  }

  if (!lowerCandidate && !minimumGuardrailEvaluated) {
    const reason = stopReason()
    if (reason) return finishBounded(reason)
    const minimumCandidate = await runEncode(
      TARGET_SIZE_MIN_QUALITY,
      minimumDimensions,
    )
    if (!minimumCandidate) return finishBounded(stopReason() ?? 'attempt_limit')
    minimumGuardrailEvaluated = true
    if (minimumCandidate.blob.size <= targetBytes) {
      lowerCandidate = minimumCandidate
    }
  }

  if (!lowerCandidate) {
    const actualMinimum = minimum as TargetSizeCandidate | null
    if (!actualMinimum || !minimumGuardrailEvaluated) {
      return finishBounded(stopReason() ?? 'attempt_limit')
    }
    return {
      ...actualMinimum,
      reachedTarget: false,
      attempts,
      stopReason: 'minimum_quality_and_dimensions',
    }
  }

  const highAtSelectedDimensions = await runEncode(
    TARGET_SIZE_MAX_QUALITY,
    { width: lowerCandidate.width, height: lowerCandidate.height },
  )
  if (!highAtSelectedDimensions) {
    return finishBounded(stopReason() ?? 'attempt_limit')
  }
  if (highAtSelectedDimensions.blob.size <= targetBytes) {
    return { ...highAtSelectedDimensions, reachedTarget: true, attempts }
  }
  return searchQuality(
    { width: lowerCandidate.width, height: lowerCandidate.height },
    lowerCandidate,
  )
}
